import { defineComponent } from "vue";
import common from "../core/core";

import { VueAutoHeight } from "@/core/vue-auto-height.component";
import type { ContactFormResponse } from "@/services/wordpress/ContactFormResponse";
import * as Sentry from "@sentry/vue";

export default defineComponent({
  name: "IPTV Email",
  components: {
    VueAutoHeight,
  },
  data() {
    return {
      contactForm: {
        name: "",
        email: "",
        message: "",
        captchaToken: "",
        captchaUsingV2: false,
        captchaExpired: false,
      },
      contactFormErrors: {
        name: null,
        email: null,
        message: null,
        captcha: null,
      },
      responseMessage: null as string,
      isSubmitPending: false,
      isSubmitOk: false,
      isSubmitFailed: false,
    };
  },
  async mounted() {
    await this.$store.direct.getters.recaptchaService.init();
  },
  methods: {
    submitContactForm(e: Event) {
      e.preventDefault();
      this.validateContactForm();
    },
    clearValidate(field: "name" | "email" | "message" | "captcha") {
      this.contactFormErrors[field] = null;
    },
    validateField(field: "name" | "email" | "message" | "captcha", required = false) {
      const getErrorText = (attrKey: string) => {
        const validateDataEl: any = this.$refs.validateData;
        return validateDataEl.getAttribute(attrKey);
      };
      const validateEmail = (email: string) => {
        return String(email)
          .toLowerCase()
          .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          );
      };
      switch (field) {
        case "email":
          this.contactFormErrors.email = null;
          if (!this.contactForm.email && required) {
            this.contactFormErrors.email = getErrorText("data-email-required");
          }
          if (this.contactForm.email && !validateEmail(this.contactForm.email)) {
            this.contactFormErrors.email = getErrorText("data-email-email");
          }
          if (this.contactForm.email && validateEmail(this.contactForm.email)) {
            this.contactFormErrors.email = false;
          }
          break;

        case "captcha":
          this.contactFormErrors.captcha = null;
          if (this.contactForm.captchaUsingV2 && (!this.contactForm.captchaToken || this.contactForm.captchaExpired)) {
            this.contactFormErrors.captcha = getErrorText("data-captcha-invalid");
          } else {
            this.contactFormErrors.captcha = false;
          }
          break;
      }
    },
    validateContactForm() {
      this.validateField("email", true);
      this.validateField("captcha", true);

      if (
        !this.contactFormErrors.name &&
        !this.contactFormErrors.email &&
        !this.contactFormErrors.message &&
        !this.contactFormErrors.captcha
      ) {
        this.formRequest();
      }
    },
    async formRequest() {
      this.isSubmitPending = true;
      this.isSubmitOk = false;
      this.isSubmitFailed = false;

      if (!this.contactForm.captchaUsingV2) {
        await this.$store.direct.getters.recaptchaService
          .getToken("iptv_email_form")
          .then((captchaToken) => {
            this.contactForm.captchaToken = captchaToken;
          })
          .catch((error) => {
            // Captcha failed
            this.isSubmitPending = false;
            this.isSubmitFailed = true;
            Sentry.captureException(error);
          });
      }

      await this.$store.direct.getters.contactFormService
        .submitIptvEmailForm({
          email: this.contactForm.email,
          captcha: this.contactForm.captchaToken,
          captcha_type: !this.contactForm.captchaUsingV2 ? "V3" : "V2",
          persistent_storage_id: this.$store.direct.getters.localStorageTagger.getTag(),
        })
        .then((response: ContactFormResponse) => {
          this.isSubmitPending = false;
          this.responseMessage = response.message;

          if (response.success && response.success === true) {
            this.isSubmitOk = true;

            this.$store.direct.getters.gtmService.trackOncePerSession("ott_email_submitted", {
              email: this.contactForm.email,
            });
          } else {
            if (!this.contactForm.captchaUsingV2 && response.code == 403) {
              this.isSubmitFailed = false;
              this.isSubmitOk = false;

              this.contactForm.captchaToken = "";
              this.contactForm.captchaUsingV2 = true;

              this.$store.direct.getters.recaptchaService.initV2(
                "recaptcha-v2",
                (token: string) => {
                  this.contactForm.captchaToken = token;
                },
                (expired: boolean) => {
                  this.contactForm.captchaExpired = expired;
                }
              );
            } else {
              this.isSubmitFailed = true;
            }
          }
        })
        .catch((error) => {
          // WP BE form handling failed
          this.isSubmitFailed = true;
          this.isSubmitPending = false;

          Sentry.captureException(error);
        });
    },
  },
  mixins: [common],
});
